import { useCallback } from 'react';
import { useLocalStorageManager } from '@/hooks/useLocalStorageManager';

interface ProjectState {
  activeTab: string;
  scrollPosition: number;
  filterStates: Record<string, any>;
  lastVisited: string;
}

export const useProjectStateManager = (projectId: string) => {
  const { getStandardValue, setStandardValue } = useLocalStorageManager();
  
  const getProjectState = useCallback((): ProjectState => {
    return getStandardValue(`project-state-${projectId}`, {
      activeTab: 'tasks',
      scrollPosition: 0,
      filterStates: {},
      lastVisited: new Date().toISOString()
    }, 'PROJECT');
  }, [projectId, getStandardValue]);
  
  const saveProjectState = useCallback((state: Partial<ProjectState>) => {
    const currentState = getProjectState();
    const newState = {
      ...currentState,
      ...state,
      lastVisited: new Date().toISOString()
    };
    setStandardValue(`project-state-${projectId}`, newState, 'PROJECT');
  }, [projectId, getProjectState, setStandardValue]);
  
  const saveActiveTab = useCallback((tab: string) => {
    saveProjectState({ activeTab: tab });
  }, [saveProjectState]);
  
  const saveScrollPosition = useCallback((position: number) => {
    saveProjectState({ scrollPosition: position });
  }, [saveProjectState]);
  
  const saveFilterState = useCallback((filterKey: string, filterValue: any) => {
    const currentState = getProjectState();
    const newFilterStates = {
      ...currentState.filterStates,
      [filterKey]: filterValue
    };
    saveProjectState({ filterStates: newFilterStates });
  }, [getProjectState, saveProjectState]);
  
  const clearProjectState = useCallback(() => {
    const storage = localStorage.getItem('local-storage-debug');
    if (storage) {
      const debugData = JSON.parse(storage);
      delete debugData[`project-state-${projectId}`];
      localStorage.setItem('local-storage-debug', JSON.stringify(debugData));
    }
  }, [projectId]);
  
  return {
    getProjectState,
    saveActiveTab,
    saveScrollPosition,
    saveFilterState,
    clearProjectState
  };
};